import type { FC, ReactNode } from 'react'
import type { FaqItem } from '@/types'

import { useId, useState } from 'react'
import { cn } from '@/lib/utils'
import { ScrollTrigger } from '@/lib/motion'

interface FaqListProps {
    items: FaqItem[]
    className?: string
}

// Accordion of questions. Answers open on a grid-rows transition (no height
// measuring), and closed answers are inert so they stay out of the tab order.
const FaqList: FC<FaqListProps> = ({ items, className }): ReactNode => {
    const baseId = useId()
    const [open, setOpen] = useState<number | null>(0)

    const toggle = (index: number): void => {
        setOpen(current => current === index ? null : index)

        // The page grows or shrinks, so scroll-linked positions below must update.
        window.setTimeout(() => ScrollTrigger.refresh(), 600)
    }

    return (
        <ul className={cn('border-t border-rule', className)}>
            {items.map((item, index) => {
                const isOpen = open === index
                const panelId = `${baseId}-panel-${index}`
                const buttonId = `${baseId}-button-${index}`

                return (
                    <li key={item.question} className='border-b border-rule'>
                        <h3>
                            <button
                                id={buttonId}
                                type='button'
                                aria-expanded={isOpen}
                                aria-controls={panelId}
                                onClick={() => toggle(index)}
                                className='group flex w-full items-center justify-between gap-6 py-6 text-left'
                            >
                                <span className='font-heading text-[clamp(1.15rem,1.7vw,1.6rem)] font-[560] leading-tight tracking-[-0.02em] transition-transform duration-500 ease-expo group-hover:translate-x-1.5'>
                                    {item.question}
                                </span>

                                <span aria-hidden='true' className='relative size-5 shrink-0'>
                                    <span className='absolute left-0 top-1/2 h-px w-5 bg-current' />
                                    <span
                                        className={cn(
                                            'absolute left-0 top-1/2 h-px w-5 bg-current transition-transform duration-500 ease-expo',
                                            isOpen ? 'rotate-0' : 'rotate-90'
                                        )}
                                    />
                                </span>
                            </button>
                        </h3>

                        <div
                            id={panelId}
                            role='region'
                            aria-labelledby={buttonId}
                            inert={!isOpen}
                            className={cn(
                                'grid transition-[grid-template-rows] duration-500 ease-quart motion-reduce:transition-none',
                                isOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
                            )}
                        >
                            <div className='overflow-hidden'>
                                <p className='max-w-[62ch] pb-7 text-lg text-quiet'>
                                    {item.answer}
                                </p>
                            </div>
                        </div>
                    </li>
                )
            })}
        </ul>
    )
}

export default FaqList
